// pasos-verificacion.js
//
// Los cuatro pasos del wizard de verificación y, sobre todo, POR CUÁL entra
// cada cuenta.
//
// Hasta el 09-sep-2026 el alta eran dos pantallas sueltas: /bienvenida (saludo
// + 4 preguntas de perfilamiento) y después /perfil/verificacion (documentos).
// Sebastián eligió la variante C del mockup
// `tools/mockups/smartdx_mockups_bienvenida_registro.html`: un solo flujo, un
// solo indicador de progreso, una sola ruta.
//
//     Bienvenida · Quién eres · Documentos · Listo
//
// Esta regla vive acá y no dentro del componente por una razón concreta: es lo
// único del wizard que se puede equivocar en silencio. Mostrarle la bienvenida
// a alguien que ya subió sus papeles, o volver a preguntarle la etapa a quien
// ya la respondió, no rompe nada visible — simplemente lo hace abandonar.

import { faltantesConocidos } from "./onboarding-completo";

export const PASOS_VERIFICACION = [
  { key: "bienvenida", label: "Bienvenida" },
  { key: "quien-eres", label: "Quién eres" },
  { key: "documentos", label: "Documentos" },
  { key: "listo", label: "Listo" },
];

export const PASO_BIENVENIDA = 0;
export const PASO_QUIEN_ERES = 1;
export const PASO_DOCUMENTOS = 2;
export const PASO_LISTO = 3;

/**
 * ¿A esta cuenta se le pide verificar que es médico (carnet + RNPI)?
 *
 * Solo a Chile (#1604, Sebastián 12-oct-2026: "que esto le salga solo a la gente
 * de Chile"). La cédula y el certificado de la Superintendencia son trámites
 * chilenos: a un médico de otro país le pedíamos un registro que no puede tener.
 * El país es el que eligió en el alta (#1590, `professional_profile.country`).
 * Vacío = cuenta anterior a #1590, que eran todas chilenas: se le sigue pidiendo.
 * El backend no exige los papeles para usar la app (#1529), así que saltarse el
 * paso no deja a nadie bloqueado.
 */
export const pideVerificacion = (profile) => {
  const pais = (profile?.country || "").trim().toUpperCase();
  return !pais || pais === "CL";
};

/**
 * Los pasos que esta cuenta ve. #1850 (Sebastián 26-oct: «la gente se sale, lo pedimos al
 * suscribirse»): el alta ya NO pide documentos, ni en Chile. En Chile el 36% terminaba el
 * onboarding en ese paso. «Documentos» solo aparece si la cuenta vino a subirlos (`?paso=documentos`,
 * desde Perfil o Ajustes), y fuera de Chile nunca (#1604). Se piden al suscribirse (#1678 parte 2).
 */
export const pasosVisibles = (profile, { forzarDocumentos = false } = {}) =>
  pideVerificacion(profile) && forzarDocumentos
    ? PASOS_VERIFICACION
    : PASOS_VERIFICACION.filter((p) => p.key !== "documentos");

/**
 * ¿Ya contestó "quién eres"?
 *
 * Cubre R22 — .claude/specs/1599-onboarding-calificar/. Desde #1599 la señal es
 * `onboarding_faltante` de /api/user/me/ (la lista de lo que le falta, calculada
 * por el backend con la misma regla que valida el PATCH): `[]` = respondió todo.
 * Solo cuentan las claves que el front sabe preguntar (`faltantesConocidos`),
 * para que una clave de un backend más nuevo no mande a nadie a una encuesta que
 * esta versión no puede mostrar. Si /me no trae la clave (backend viejo) cae a la
 * regla de antes: alcanza con la etapa. `etapaReciente` cubre el instante en que
 * la acaba de elegir y todavía no volvió en /me.
 */
export const yaRespondioPerfilamiento = (profile, etapaReciente, faltante) => {
  if (Array.isArray(faltante)) return faltantesConocidos(faltante).length === 0;
  return Boolean((etapaReciente || profile?.etapa || "").trim());
};

/**
 * ¿Ya entregó los papeles?
 *
 * REJECTED queda deliberadamente afuera: tiene documentos cargados, pero un
 * admin los rechazó y lo que le toca es volver a subirlos. Mandarlo al paso
 * "Listo" sería dejarlo mirando el resumen de algo que ya no sirve.
 */
export const yaEnvioDocumentos = (profile) => {
  const status = profile?.verification_status || "NOT_SUBMITTED";
  if (status === "REJECTED") return false;
  return (
    status === "PENDING" || status === "APPROVED" || Boolean(profile?.has_id_document)
  );
};

/**
 * Por dónde entra esta cuenta — y también hasta dónde puede retroceder.
 *
 * Es el mismo número para las dos cosas a propósito: un paso que ya está
 * resuelto no se muestra al entrar NI se alcanza con el botón "Atrás". Si
 * fueran dos números distintos, "Atrás" terminaría devolviendo a la pantalla de
 * bienvenida a alguien que ya subió su carnet.
 */
// Ronda 2 (#1401, revisor): quien entra a subir sus documentos (el chip de
// primeros pasos, #1529: ya no hay gate ni corte por documentos) necesita
// subir el carnet YA, no leer un saludo ni contestar 4 preguntas opcionales
// de perfilamiento primero. `forzarDocumentos` salta directo al paso 3, salvo
// que ya haya entregado papeles (ahí manda `yaEnvioDocumentos` igual, para
// no mostrarle de nuevo un paso resuelto).
// Cubre R9 — .claude/specs/rescate-inactivos/. `reintro` (cuenta del rescate de
// inactivos): entra por la Bienvenida aunque ya esté aprobada o haya respondido
// las preguntas. `forzarDocumentos` sigue mandando: quien viene a subir papeles
// no lee un saludo.
// #1604: fuera de Chile no hay paso de documentos. `?paso=documentos` no lo
// abre, y quien ya respondió las preguntas cae en Listo.
export const primerPasoPendiente = (profile, etapaReciente, opciones = {}) => {
  const conDocumentos = pideVerificacion(profile);
  const forzarDocumentos = conDocumentos && opciones.forzarDocumentos;
  if (opciones.reintro && !forzarDocumentos) return PASO_BIENVENIDA;
  if (yaEnvioDocumentos(profile)) return PASO_LISTO;
  if (forzarDocumentos) return PASO_DOCUMENTOS;
  // #1850: quien ya respondió cae en Listo, también en Chile (los documentos se piden al suscribirse).
  if (yaRespondioPerfilamiento(profile, etapaReciente, opciones.faltante)) return PASO_LISTO;
  return PASO_BIENVENIDA;
};

/**
 * Qué papeles anunciarle en la bienvenida. Tres respuestas, no dos.
 *
 * Al interno NO se le pide el certificado de la Superintendencia: no está
 * titulado, no tiene número de registro, y ese registro no puede tenerlo.
 *
 * El tercer caso existe por un error que costó caro: una cuenta recién creada
 * con Google llega sin `profession` y sin `etapa` —la etapa se pregunta recién
 * en el paso siguiente— y caía al caso "titulado". O sea que TODO interno leía
 * "saca tu certificado de la Superintendencia", iba al RNPI, no lo encontraba
 * (no puede) y abandonaba: exactamente el abandono que esta pantalla vino a
 * evitar. Cuando no se sabe, se dicen los dos casos; nunca se adivina.
 */
export const quePapelesPedir = (profile, etapaElegida) => {
  // #1604: fuera de Chile no se piden papeles; la bienvenida no los anuncia.
  if (!pideVerificacion(profile)) return "ninguno";
  const profesion = profile?.profession || "";
  const etapa = etapaElegida || profile?.etapa || "";
  if (!profesion && !etapa) return "aun-no-sabemos";
  // #1738 R10: el estudiante pide lo mismo que el interno (solo el carnet).
  if (etapa === "interno" || etapa === "estudiante" || profesion === "INTERNO" || profesion === "ESTUDIANTE") {
    return "solo-carnet";
  }
  return "carnet-y-certificado";
};
